'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Loader2, Lock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSelect } from '@/components/forms/form-select';
import { FormTextarea } from '@/components/forms/form-textarea';
import { FormCombobox } from '@/components/forms/form-combobox';
import { FormMultiCombobox } from '@/components/forms/form-multi-combobox';
import { editShipmentSchema, type EditShipmentFormValues } from '../schemas';
import { driversApi, driverKeys, type Driver } from '@/features/drivers';
import { packingApi, boxKeys, type Box } from '@/features/packing';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { shippingQueries } from '../services/shipping.queries';
import { useUpdateShipmentMutation } from '../services/shipping.mutations';
import type { Shipment } from '../types';

interface ShippingFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment?: Shipment | null;
}

export function ShippingFormDialog({
  open,
  onOpenChange,
  shipment
}: ShippingFormDialogProps) {
  const t = useTranslations('shipping');
  const tv = useTranslations('validation');

  const schema = useMemo(() => editShipmentSchema(tv), [tv]);

  const form = useForm<EditShipmentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      priority: 'normal',
      location: '',
      notes: '',
      driver_id: '',
      box_ids: []
    }
  });

  const updateMutation = useUpdateShipmentMutation();

  const { data: freshShipmentResponse, isFetching: isFetchingDetail } =
    useQuery({
      ...shippingQueries.detail(shipment?.id ?? 0),
      enabled: open && !!shipment?.id
    });
  const freshShipment = freshShipmentResponse?.data;

  useEffect(() => {
    if (!open || !freshShipment) return;
    form.reset({
      priority: freshShipment.priority ?? 'normal',
      location: freshShipment.location ?? '',
      notes: freshShipment.notes ?? '',
      driver_id: freshShipment.driver_profile_id?.toString() ?? '',
      box_ids: freshShipment.boxes?.map((b) => b.id.toString()) ?? []
    });
  }, [freshShipment, open, form]);

  // Driver combobox — infinite searchable list
  const mapDriverOption = useCallback(
    (driver: Driver) => ({
      value: driver.driver_profile?.id?.toString() ?? '',
      label: driver.name ?? t('unknownDriver')
    }),
    [t]
  );
  const driverSelect = useInfiniteSelect<Driver>({
    queryKey: [...driverKeys.lists(), 'available'],
    queryFn: (params) =>
      driversApi.getAll({
        ...params,
        availability: ['available']
      }),
    mapOption: mapDriverOption,
    enabled: open
  });

  // Boxes — infinite searchable multi-select
  const mapBoxOption = useCallback(
    (box: Box) => ({
      value: box.id.toString(),
      label: box.box_number
    }),
    []
  );
  const boxSelect = useInfiniteSelect<Box>({
    queryKey: boxKeys.lists(),
    queryFn: (params) => packingApi.getAll(params),
    mapOption: mapBoxOption,
    params: { status: 'ready_for_shipping' },
    enabled: open
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) form.reset();
    onOpenChange(nextOpen);
  };

  async function onSubmit(values: EditShipmentFormValues) {
    if (!shipment) return;

    const driverChanged = form.formState.dirtyFields.driver_id;

    await updateMutation.mutateAsync({
      id: shipment.id,
      data: {
        priority: values.priority,
        location: values.location || undefined,
        notes: values.notes || undefined,
        ...(driverChanged
          ? { driver_id: values.driver_id ? Number(values.driver_id) : null }
          : {}),
        ...(!boxesLocked && values.box_ids.length > 0
          ? { box_ids: values.box_ids.map(Number) }
          : {})
      }
    });
    handleOpenChange(false);
  }

  const priorityOptions = [
    { value: 'normal', label: t('priorityNormal') },
    { value: 'high', label: t('priorityHigh') },
    { value: 'urgent', label: t('priorityUrgent') }
  ];

  const activeShipment = freshShipment ?? shipment;
  const boxesLocked =
    activeShipment?.status === 'in_transit' ||
    activeShipment?.status === 'received';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>{t('editShipment')}</DialogTitle>
        </DialogHeader>

        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className='space-y-4'>
            <FormSelect
              control={form.control}
              name='priority'
              label={t('priority')}
              options={priorityOptions}
              required
            />

            <FormInput
              control={form.control}
              name='location'
              label={t('location')}
              placeholder={t('locationPlaceholder')}
            />

            <FormCombobox
              control={form.control}
              name='driver_id'
              label={t('driverColumn')}
              placeholder={activeShipment?.driver?.name ?? t('selectDriver')}
              searchPlaceholder={t('searchDriver')}
              emptyMessage={t('noDriversFound')}
              infiniteConfig={driverSelect}
            />

            {boxesLocked ? (
              <div className='text-muted-foreground flex items-center gap-2 rounded-md border border-dashed p-3 text-sm'>
                <Lock className='h-4 w-4 shrink-0' />
                <span>{t('boxSelectionDisabled')}</span>
              </div>
            ) : (
              <FormMultiCombobox
                control={form.control}
                name='box_ids'
                label={t('selectBoxes')}
                placeholder={t('selectBoxes')}
                searchPlaceholder={t('searchBoxes')}
                emptyMessage={t('noBoxesFound')}
                infiniteConfig={boxSelect}
              />
            )}

            <FormTextarea
              control={form.control}
              name='notes'
              label={t('notes')}
              placeholder={t('notesPlaceholder')}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleOpenChange(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                type='submit'
                disabled={updateMutation.isPending || isFetchingDetail}
              >
                {(updateMutation.isPending || isFetchingDetail) && (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                )}
                {t('saveChanges')}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
